import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';

const SocketContext = createContext(null);

// 全部事件列表
const EVENTS = [
  'data_refresh',       // 用户工作台数据变化（接量/完成）
  'stats_refresh',      // 管理后台统计刷新
  'settlement_updated', // 结算完成
  'users_updated',      // 用户列表变化
  'pool_updated',       // 数据池变化（导入/清理）
  'verification_code_updated', // 验证码更新
  'work_hours_updated', // 工作时间更新
  'qrcode_updated',     // 二维码更新
  'work_time_ended',    // 工作时间结束（精准触发一次）
  'admin_notification', // 管理员/代理系统提示（用户完成、接量、上传图片等）
  'push_message',       // 1对1 推送消息（工资通知等）
  'image_stats_refresh', // 管理员代上传图片后通知目标用户工作台刷新分组
  'ocr_batch_done',     // OCR 批次识别完成
  'ocr_progress',       // OCR 逐张进度推送
  'rate_ctrl_updated',  // 转换率管控配置变更，前端需刷新配额
  'video_review_submitted', // 用户提交视频，通知管理员/代理刷新审核列表
  'maintenance_updated',   // 系统维护模式开关变更（payload: { enabled }）
  // ── 证券业务 ──
  'puzzle_pool_updated',        // 数据包池变化（导入/被领取），接量页刷新“还剩几个包”
  'puzzle_session_updated',     // 本人领包/完成本次/记录确认，工作台刷新号码列表
  'puzzle_updated',             // 证券记录变化（核实/更正/结算），刷新记录与统计
  'puzzle_settlement_updated',  // 证券结算完成，结算页刷新
  'puzzle_materials_updated',   // 获客素材（证券 4 张步骤图）更新
];

export function SocketProvider({ children }) {
  const { user } = useAuth();
  const socketRef = useRef(null);
  const listenersRef = useRef([]);
  const [connected, setConnected] = useState(true);
  const wasConnectedRef = useRef(false); // 标记是否曾经连上过（用于区分首次连接和断线重连）

  useEffect(() => {
    if (!user) {
      if (socketRef.current) { socketRef.current.disconnect(); socketRef.current = null; }
      return;
    }
    const socket = io(window.location.origin, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity,  // 无限重试
      reconnectionDelay: 1000,         // 1 秒后开始重连
      reconnectionDelayMax: 10000,     // 最长等 10 秒
      timeout: 20000,
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('register', { token: localStorage.getItem('token') });
      setConnected(true);
      // 断线重连后通知所有监听者刷新数据
      if (wasConnectedRef.current) {
        listenersRef.current.forEach(fn => fn('stats_refresh', {}));
        listenersRef.current.forEach(fn => fn('data_refresh', {}));
      }
      wasConnectedRef.current = true;
    });

    socket.on('disconnect', () => {
      setConnected(false);
    });

    socket.on('connect_error', () => {
      setConnected(false);
    });

    // 服务端校验 token 失败（未登录/过期）→ 清理本地登录态，触发跳转登录
    socket.on('register_error', () => {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    });

    EVENTS.forEach(evt => {
      socket.on(evt, (payload) => {
        listenersRef.current.forEach(fn => fn(evt, payload));
      });
    });

    return () => { socket.disconnect(); socketRef.current = null; wasConnectedRef.current = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const subscribe = React.useCallback((callback) => {
    listenersRef.current.push(callback);
    return () => { listenersRef.current = listenersRef.current.filter(fn => fn !== callback); };
  }, []); // 空依赖：socket 生命周期内 subscribe 引用不变

  return (
    <SocketContext.Provider value={{ subscribe, connected }}>
      {/* 断线提示条：连接断开时显示，重连成功自动消失 */}
      {!connected && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
          background: '#ff9500', color: '#fff',
          textAlign: 'center', fontSize: 13, fontWeight: 600,
          padding: '8px 16px', letterSpacing: 0.2,
        }}>
          网络连接已断开，正在重连…
        </div>
      )}
      {children}
    </SocketContext.Provider>
  );
}

export function useSocket() {
  return useContext(SocketContext);
}
